/**
 * EventsService — unified calendar event source.
 *
 * Aggregates THREE event streams into one query surface:
 *   1. Public holidays from Nager.Date  (100+ countries, no auth, free, CORS-OK)
 *      https://date.nager.at/api/v3/PublicHolidays/{year}/{country}
 *   2. Islamic special days computed offline via IslamicCalendar/HijriDate
 *      (Eid al-Fitr, Eid al-Adha, Arafah, Ramadan window, Mawlid, Ashura,
 *       Hijri new year, Laylat al-Qadr) — projected for the requested range.
 *   3. Personal events stored in localStorage (free-text, user-added).
 *
 * Strategy:
 *   - Nager.Date fetches are cached per (year, countryCode) for 30 days
 *     in localStorage (`aweni_holidays_<CC>_<YYYY>`). Cache survives offline
 *     reloads. A ttl miss fires a background refresh but immediately returns
 *     the stale cache so the calendar never blocks.
 *   - Islamic events are computed every call (cheap, pure JS).
 *   - Personal events live in `aweni_personal_events` keyed by id.
 *
 * Boundary: this is NOT religious content (no Quran, no hadith). Just
 * date metadata. So Nager.Date public-holiday data is fair game even though
 * Nager is non-Islamic; we never paraphrase or translate religious text.
 */

import { gregorianToHijri } from './HijriDate';
import type { CalendarEvent } from '../modules/home/types';

const HOLIDAY_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const PERSONAL_LS_KEY = 'aweni_personal_events';
const HOLIDAY_LS_PREFIX = 'aweni_holidays_';

interface NagerHolidayRaw {
  date: string;
  localName: string;
  name: string;
  countryCode: string;
  fixed?: boolean;
  global?: boolean;
  types?: string[];
}

interface CachedHolidayBundle {
  fetchedAt: number;
  holidays: NagerHolidayRaw[];
}

export class EventsService {
  /** In-flight fetches keyed by `${cc}_${year}` to dedupe parallel calls. */
  private inflight: Map<string, Promise<NagerHolidayRaw[]>> = new Map();

  /**
   * Get all events overlapping [startISO, endISO] (inclusive). Returns
   * synchronously-available events (cached holidays + Islamic + personal)
   * if a refresh is needed it fires-and-forgets in the background.
   *
   * @param startISO  inclusive start, YYYY-MM-DD
   * @param endISO    inclusive end,   YYYY-MM-DD
   * @param countryCode  ISO 3166-1 alpha-2, used to filter holidays. Pass null
   *                     to skip Nager.Date entirely (Islamic + personal only).
   * @param options.includeIslamic  default true
   * @param options.includeHolidays default true (only meaningful w/ countryCode)
   * @param options.includePersonal default true
   */
  async getEventsInRange(
    startISO: string,
    endISO: string,
    countryCode: string | null,
    options: {
      includeIslamic?: boolean;
      includeHolidays?: boolean;
      includePersonal?: boolean;
      locale?: string;
    } = {},
  ): Promise<CalendarEvent[]> {
    const includeIslamic = options.includeIslamic !== false;
    const includeHolidays = options.includeHolidays !== false && countryCode !== null;
    const includePersonal = options.includePersonal !== false;
    const locale = options.locale || 'en';

    const events: CalendarEvent[] = [];

    if (includeHolidays && countryCode) {
      const years = yearsInRange(startISO, endISO);
      for (const y of years) {
        const raw = await this.getHolidaysForYear(countryCode, y);
        for (const h of raw) {
          if (h.date < startISO || h.date > endISO) continue;
          events.push({
            id: `holiday_${countryCode}_${h.date}`,
            date: h.date,
            title: pickHolidayName(h, locale),
            description: h.types?.join(', '),
            category: 'holiday',
            source: 'nager',
            countryCode: h.countryCode,
          });
        }
      }
    }

    if (includeIslamic) {
      events.push(...this.computeIslamicEvents(startISO, endISO));
    }

    if (includePersonal) {
      const all = this.listPersonalEvents();
      for (const p of all) {
        if (p.date >= startISO && p.date <= endISO) events.push(p);
      }
    }

    // Stable order: by date asc, then category priority (holiday > islamic > personal)
    events.sort((a, b) => a.date.localeCompare(b.date) || categoryRank(a) - categoryRank(b));
    return events;
  }

  /**
   * Synchronous variant — only returns Islamic + personal + cached holidays.
   * Used when the calendar must paint immediately. Calling code can `await
   * getEventsInRange()` afterwards to upgrade with fresh holiday data.
   */
  getEventsInRangeSync(
    startISO: string,
    endISO: string,
    countryCode: string | null,
    options: {
      includeIslamic?: boolean;
      includeHolidays?: boolean;
      includePersonal?: boolean;
      locale?: string;
    } = {},
  ): CalendarEvent[] {
    const includeIslamic = options.includeIslamic !== false;
    const includeHolidays = options.includeHolidays !== false && countryCode !== null;
    const includePersonal = options.includePersonal !== false;
    const locale = options.locale || 'en';

    const events: CalendarEvent[] = [];

    if (includeHolidays && countryCode) {
      const years = yearsInRange(startISO, endISO);
      for (const y of years) {
        const cached = this.readHolidayCache(countryCode, y);
        if (!cached) continue;
        for (const h of cached.holidays) {
          if (h.date < startISO || h.date > endISO) continue;
          events.push({
            id: `holiday_${countryCode}_${h.date}`,
            date: h.date,
            title: pickHolidayName(h, locale),
            description: h.types?.join(', '),
            category: 'holiday',
            source: 'nager',
            countryCode: h.countryCode,
          });
        }
      }
    }

    if (includeIslamic) {
      events.push(...this.computeIslamicEvents(startISO, endISO));
    }

    if (includePersonal) {
      const all = this.listPersonalEvents();
      for (const p of all) if (p.date >= startISO && p.date <= endISO) events.push(p);
    }

    events.sort((a, b) => a.date.localeCompare(b.date) || categoryRank(a) - categoryRank(b));
    return events;
  }

  // -------- Holiday cache + fetch -----------------------------------------

  /** Returns holidays from cache or network. Network failure → returns []. */
  async getHolidaysForYear(countryCode: string, year: number): Promise<NagerHolidayRaw[]> {
    const cached = this.readHolidayCache(countryCode, year);
    const fresh = cached && Date.now() - cached.fetchedAt < HOLIDAY_TTL_MS;
    if (fresh) return cached.holidays;

    // Stale or missing — return cache immediately (if any) and refresh
    // in background. Caller shouldn't block on this.
    if (cached) {
      this.refreshHolidaysInBackground(countryCode, year);
      return cached.holidays;
    }

    // First-ever fetch — must wait. Dedupe parallel callers.
    const key = `${countryCode}_${year}`;
    let pending = this.inflight.get(key);
    if (!pending) {
      pending = this.fetchHolidaysFromNetwork(countryCode, year);
      this.inflight.set(key, pending);
      pending.finally(() => this.inflight.delete(key));
    }
    try {
      return await pending;
    } catch {
      return [];
    }
  }

  private refreshHolidaysInBackground(countryCode: string, year: number): void {
    const key = `${countryCode}_${year}`;
    if (this.inflight.has(key)) return;
    const pending = this.fetchHolidaysFromNetwork(countryCode, year);
    this.inflight.set(key, pending);
    pending.finally(() => this.inflight.delete(key));
  }

  private async fetchHolidaysFromNetwork(countryCode: string, year: number): Promise<NagerHolidayRaw[]> {
    const url = `https://date.nager.at/api/v3/PublicHolidays/${year}/${encodeURIComponent(countryCode)}`;
    try {
      const resp = await fetch(url, { cache: 'no-store' });
      if (!resp.ok) return [];
      const json = (await resp.json()) as NagerHolidayRaw[];
      if (!Array.isArray(json)) return [];
      this.writeHolidayCache(countryCode, year, json);
      return json;
    } catch {
      return [];
    }
  }

  private readHolidayCache(countryCode: string, year: number): CachedHolidayBundle | null {
    try {
      const raw = localStorage.getItem(`${HOLIDAY_LS_PREFIX}${countryCode}_${year}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.fetchedAt === 'number' && Array.isArray(parsed?.holidays)) return parsed;
      return null;
    } catch {
      return null;
    }
  }

  private writeHolidayCache(countryCode: string, year: number, holidays: NagerHolidayRaw[]): void {
    try {
      const bundle: CachedHolidayBundle = { fetchedAt: Date.now(), holidays };
      localStorage.setItem(`${HOLIDAY_LS_PREFIX}${countryCode}_${year}`, JSON.stringify(bundle));
    } catch {
      /* quota */
    }
  }

  // -------- Islamic events ------------------------------------------------

  /**
   * Compute Islamic events visible in [startISO, endISO]. We sweep daily
   * across the range (cheap — pure JS) and flag dated specials. For
   * recurring weekly Friday we omit it (too noisy; calendar already shows
   * day-of-week, and the Hero already nudges Jumu'ah on Fridays).
   */
  private computeIslamicEvents(startISO: string, endISO: string): CalendarEvent[] {
    const out: CalendarEvent[] = [];
    const start = parseISO(startISO);
    const end = parseISO(endISO);
    if (!start || !end) return out;

    const cursor = new Date(start);
    while (cursor <= end) {
      const h = gregorianToHijri(cursor);
      const iso = toISO(cursor);
      const m = h.month;
      const d = h.day;
      const id = (slug: string) => `islamic_${slug}_${iso}`;

      // Eid al-Fitr (1 Shawwāl) — single anchor day.
      if (m === 10 && d === 1) {
        out.push({ id: id('eid_fitr'), date: iso, title: 'Eid al-Fitr',
          category: 'islamic', source: 'islamic_calendar' });
      }
      // Eid al-Adha (10 Dhū al-Ḥijjah).
      if (m === 12 && d === 10) {
        out.push({ id: id('eid_adha'), date: iso, title: 'Eid al-Adha',
          category: 'islamic', source: 'islamic_calendar' });
      }
      // Arafah day (9 Dhū al-Ḥijjah).
      if (m === 12 && d === 9) {
        out.push({ id: id('arafah'), date: iso, title: 'Day of Arafah',
          category: 'islamic', source: 'islamic_calendar' });
      }
      // Ramadan start (1 Ramaḍān).
      if (m === 9 && d === 1) {
        out.push({ id: id('ramadan_start'), date: iso, title: 'Start of Ramadan',
          category: 'islamic', source: 'islamic_calendar' });
      }
      // Laylat al-Qadr (27 Ramaḍān, most-attested).
      if (m === 9 && d === 27) {
        out.push({ id: id('laylat_qadr'), date: iso, title: 'Laylat al-Qadr',
          category: 'islamic', source: 'islamic_calendar' });
      }
      // Hijri new year (1 Muḥarram).
      if (m === 1 && d === 1) {
        out.push({ id: id('hijri_new_year'), date: iso, title: 'Hijri New Year',
          category: 'islamic', source: 'islamic_calendar' });
      }
      // Ashura (10 Muḥarram).
      if (m === 1 && d === 10) {
        out.push({ id: id('ashura'), date: iso, title: 'Day of Ashura',
          category: 'islamic', source: 'islamic_calendar' });
      }
      // Mawlid an-Nabi (12 Rabīʿ al-Awwal).
      if (m === 3 && d === 12) {
        out.push({ id: id('mawlid'), date: iso, title: 'Mawlid an-Nabi',
          category: 'islamic', source: 'islamic_calendar' });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }

  // -------- Personal events ----------------------------------------------

  listPersonalEvents(): CalendarEvent[] {
    try {
      const raw = localStorage.getItem(PERSONAL_LS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersonalEvent);
    } catch {
      return [];
    }
  }

  addPersonalEvent(date: string, title: string, description?: string): CalendarEvent {
    const ev: CalendarEvent = {
      id: `pe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      date,
      title: title.trim(),
      description: description?.trim() || undefined,
      category: 'personal',
      source: 'personal',
    };
    const all = this.listPersonalEvents();
    all.push(ev);
    this.persistPersonal(all);
    return ev;
  }

  removePersonalEvent(id: string): boolean {
    const all = this.listPersonalEvents();
    const next = all.filter((e) => e.id !== id);
    if (next.length === all.length) return false;
    this.persistPersonal(next);
    return true;
  }

  private persistPersonal(events: CalendarEvent[]): void {
    try {
      localStorage.setItem(PERSONAL_LS_KEY, JSON.stringify(events));
    } catch {
      /* quota */
    }
  }
}

// ----- helpers ------------------------------------------------------------

function yearsInRange(startISO: string, endISO: string): number[] {
  const a = parseInt(startISO.slice(0, 4), 10);
  const b = parseInt(endISO.slice(0, 4), 10);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return [];
  const out: number[] = [];
  for (let y = a; y <= b; y++) out.push(y);
  return out;
}

function pickHolidayName(h: NagerHolidayRaw, locale: string): string {
  // Nager.Date `name` is always English, `localName` is in the country's
  // primary language. For French/Arabic/Spanish/German user locale we still
  // prefer localName if the country language matches reasonably; otherwise
  // English. Heuristic: when locale starts with the country's typical
  // language code we'd surface localName, else name. Cheap fallback: prefer
  // localName when it exists and looks non-trivial.
  if (locale.startsWith('fr') && /[A-Za-zÀ-ÿ]/.test(h.localName)) return h.localName;
  if (locale.startsWith('ar') && /[؀-ۿ]/.test(h.localName)) return h.localName;
  return h.name || h.localName;
}

function categoryRank(e: CalendarEvent): number {
  switch (e.category) {
    case 'holiday':  return 0;
    case 'islamic':  return 1;
    case 'cultural': return 2;
    case 'personal': return 3;
  }
}

function parseISO(iso: string): Date | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return isNaN(d.getTime()) ? null : d;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function isPersonalEvent(v: unknown): v is CalendarEvent {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.date === 'string' && typeof o.title === 'string';
}

let _instance: EventsService | null = null;
export function getEventsService(): EventsService {
  if (!_instance) _instance = new EventsService();
  return _instance;
}
